/**
 * ChecklistSuggestions Component
 *
 * Displays suggested checklist item matches for a document with
 * batch accept/reject functionality. Grouped by category with
 * collapsible cards. Reused in both upload sheet and document detail views.
 */

import { useState, useCallback, useEffect, useMemo } from "react";
import {
  CheckSquare,
  Square,
  Loader2,
  ChevronDown,
  CheckCircle2,
  BookOpen,
  FileText,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import type { Id } from "@/packages/types";
import {
  useChecklistSuggestions,
  type ResolvedSuggestion,
} from "@/hooks/use-checklist-suggestions";
import { Button } from "@/components/ui/primitives";
import { useTranslation } from "@/packages/utils";

interface ChecklistSuggestionsProps {
  documentId: Id<"documents">;
  onComplete?: () => void;
  /** Controlled selection: if provided, component is controlled and will call onSelectedIdsChange when toggles happen */
  selectedIds?: Set<Id<"checklistItems">>;
  onSelectedIdsChange?: (ids: Set<Id<"checklistItems">>) => void;
  /** When false, hide the per-document confirm button (used by the upload sheet global confirm) */
  showConfirmButton?: boolean;
  /** Parent-driven confirmed state — when provided the component will show the success tag */
  externalConfirmed?: {
    total: number;
    accepted: number;
    items: ResolvedSuggestion[];
  } | null;
  /** Notifies parent when suggestions for this document are loaded */
  onSuggestionsReady?: (suggestions: ResolvedSuggestion[]) => void;
}

interface CategoryGroup {
  categoryId: string;
  categoryName: string;
  auditId: string;
  auditName: string;
  suggestions: ResolvedSuggestion[];
}

function ConfidenceBadge({ confidence }: { confidence: string }) {
  const colorMap: Record<string, string> = {
    high: "bg-emerald-500/15 text-emerald-400 border-emerald-500/20",
    medium: "bg-amber-500/15 text-amber-400 border-amber-500/20",
    low: "bg-zinc-500/15 text-zinc-400 border-zinc-500/20",
  };
  const colors = colorMap[confidence] ?? colorMap.low;

  return (
    <span
      className={`text-[10px] font-medium px-1.5 py-0.5 rounded border ${colors}`}
    >
      {confidence}
    </span>
  );
}

function ClauseRefPill({ clause }: { clause: string }) {
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-sky-500/10 text-sky-400 border border-sky-500/15">
      <BookOpen size={10} />
      {clause}
    </span>
  );
}

function SuggestionCard({
  suggestion,
  isSelected,
  onToggle,
}: {
  suggestion: ResolvedSuggestion;
  isSelected: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation("documents");
  const [isExpanded, setIsExpanded] = useState(false);

  const hasExpandableContent =
    suggestion.relevantExcerpt ||
    suggestion.clauseRefs.length > 0 ||
    suggestion.exampleDocuments.length > 0;

  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden">
      {/* Collapsed row: checkbox + clickable title area */}
      {/* biome-ignore lint/a11y/noStaticElementInteractions: The role and handlers are conditionally applied only when content is expandable */}
      <div
        className={`flex items-center gap-2.5 p-3 ${hasExpandableContent ? "cursor-pointer hover:bg-card-hover transition-colors" : ""}`}
        onClick={
          hasExpandableContent ? () => setIsExpanded(!isExpanded) : undefined
        }
        onKeyDown={
          hasExpandableContent
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setIsExpanded(!isExpanded);
                }
              }
            : undefined
        }
        role={hasExpandableContent ? "button" : undefined}
        tabIndex={hasExpandableContent ? 0 : undefined}
      >
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
          className="shrink-0 text-muted hover:text-primary transition-colors cursor-pointer"
        >
          {isSelected ? (
            <CheckSquare size={16} className="text-accent" />
          ) : (
            <Square size={16} />
          )}
        </button>

        <p className="text-[13px] text-primary font-medium truncate flex-1 text-left">
          {suggestion.title}
        </p>

        <ConfidenceBadge confidence={suggestion.confidence} />

        {hasExpandableContent && (
          <ChevronDown
            size={14}
            className={`shrink-0 text-muted transition-transform ${isExpanded ? "rotate-180" : ""}`}
          />
        )}
      </div>

      {/* Expanded details */}
      <AnimatePresence>
        {isExpanded && hasExpandableContent && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="overflow-hidden"
          >
            <div className="px-3 pb-3 space-y-2.5 border-t border-border/50 pt-2.5 ml-[34px]">
              {/* Clause refs */}
              {suggestion.clauseRefs.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {suggestion.clauseRefs.map((clause) => (
                    <ClauseRefPill key={clause} clause={clause} />
                  ))}
                </div>
              )}

              {/* AI reason */}
              <p className="text-[12px] text-secondary leading-relaxed">
                {suggestion.reason}
              </p>

              {/* Example documents */}
              {suggestion.exampleDocuments.length > 0 && (
                <div className="space-y-1">
                  <p className="text-[10px] uppercase text-muted font-medium tracking-wider">
                    {t("upload.suggestions.exampleDocuments")}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {suggestion.exampleDocuments.map((doc) => (
                      <span
                        key={doc.name}
                        className="inline-flex items-center gap-1 text-[11px] text-muted px-1.5 py-0.5 rounded bg-card-hover"
                      >
                        <FileText size={10} />
                        {doc.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Relevant excerpt */}
              {suggestion.relevantExcerpt && (
                <p className="text-[11px] text-muted italic px-2 py-1.5 bg-card-hover rounded">
                  &ldquo;{suggestion.relevantExcerpt}&rdquo;
                </p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function ChecklistSuggestions({
  documentId,
  onComplete,
  selectedIds: selectedIdsProp,
  onSelectedIdsChange,
  showConfirmButton = true,
  externalConfirmed = null,
  onSuggestionsReady,
}: ChecklistSuggestionsProps) {
  const { t } = useTranslation("documents");
  const { suggestions, acceptSuggestions, dismissAll, isAccepting } =
    useChecklistSuggestions(documentId);

  // Internal selection state (uncontrolled mode)
  const [internalSelectedIds, setInternalSelectedIds] = useState<
    Set<Id<"checklistItems">>
  >(() => new Set(suggestions.map((s) => s.checklistItemId)));

  // Keep local confirmed state for success UI; parent can drive this via externalConfirmed
  const [confirmed, setConfirmed] = useState<{
    total: number;
    accepted: number;
    items: ResolvedSuggestion[];
  } | null>(null);
  const [successExpanded, setSuccessExpanded] = useState(false);

  // Notify parent when suggestions load
  useEffect(() => {
    onSuggestionsReady?.(suggestions);
  }, [suggestions, onSuggestionsReady]);

  // Sync externalConfirmed into local confirmed state when provided
  useEffect(() => {
    if (externalConfirmed) setConfirmed(externalConfirmed);
  }, [externalConfirmed]);

  // Update internal selections when suggestions load (only in uncontrolled mode)
  useEffect(() => {
    if (suggestions.length > 0 && selectedIdsProp === undefined) {
      setInternalSelectedIds(
        new Set(suggestions.map((s) => s.checklistItemId)),
      );
    }
  }, [suggestions, selectedIdsProp]);

  // Current selection (controlled if prop provided)
  const currentSelectedIds = selectedIdsProp ?? internalSelectedIds;

  // Group suggestions by category
  const categoryGroups: CategoryGroup[] = useMemo(() => {
    const groupMap = new Map<string, CategoryGroup>();
    for (const s of suggestions) {
      const key = s.categoryId ?? "uncategorized";
      const existing = groupMap.get(key);
      if (existing) {
        existing.suggestions.push(s);
      } else {
        groupMap.set(key, {
          categoryId: key,
          categoryName: s.categoryName,
          auditId: s.auditId ?? "",
          auditName: s.auditName ?? "",
          suggestions: [s],
        });
      }
    }
    return [...groupMap.values()];
  }, [suggestions]);

  const toggleSelection = useCallback(
    (id: Id<"checklistItems">) => {
      if (onSelectedIdsChange) {
        const next = new Set(currentSelectedIds);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        onSelectedIdsChange(next);
        return;
      }

      setInternalSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    },
    [onSelectedIdsChange, currentSelectedIds],
  );

  const handleConfirm = async () => {
    const acceptedItems = suggestions.filter((s) =>
      currentSelectedIds.has(s.checklistItemId),
    );
    const accepted = acceptedItems.map((s) => s.checklistItemId);
    const rejected = suggestions
      .filter((s) => !currentSelectedIds.has(s.checklistItemId))
      .map((s) => s.checklistItemId);

    // Capture confirmed data before mutation clears the suggestions query
    const confirmedData = {
      total: suggestions.length,
      accepted: acceptedItems.length,
      items: acceptedItems,
    };

    await acceptSuggestions(documentId, accepted, rejected);
    setConfirmed(confirmedData);
  };

  const handleDismiss = async () => {
    await dismissAll(documentId);
    onComplete?.();
  };

  if (suggestions.length === 0 && !confirmed) return null;

  // Success state after confirmation
  if (confirmed) {
    return (
      <div className="space-y-2">
        <button
          type="button"
          onClick={() => setSuccessExpanded((prev) => !prev)}
          className="flex items-center gap-2 w-full px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20 cursor-pointer transition-colors hover:bg-emerald-500/15"
        >
          <CheckCircle2 size={14} className="text-emerald-500 shrink-0" />
          <p className="text-[12px] text-emerald-400 flex-1 text-left">
            {t("upload.suggestions.confirmed", {
              total: confirmed.total,
              accepted: confirmed.accepted,
            })}
          </p>
          <ChevronDown
            size={14}
            className={`shrink-0 text-emerald-400 transition-transform ${successExpanded ? "rotate-180" : ""}`}
          />
        </button>

        <AnimatePresence>
          {successExpanded && confirmed.items.length > 0 && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="overflow-hidden"
            >
              <div className="space-y-1.5 px-1">
                {confirmed.items.map((item) => (
                  <div
                    key={item.checklistItemId}
                    className="flex items-center gap-2 px-2.5 py-1.5 rounded-md bg-card border border-border"
                  >
                    <CheckCircle2
                      size={12}
                      className="text-emerald-500 shrink-0"
                    />
                    <p className="text-[12px] text-primary truncate flex-1">
                      {item.title}
                    </p>
                    <span className="text-[10px] text-muted">
                      {item.categoryName}
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-[11px] uppercase text-muted font-medium tracking-wider">
          {t("upload.suggestions.title", {
            count: suggestions.length,
          })}
        </p>
        <button
          type="button"
          onClick={() => {
            if (currentSelectedIds.size === suggestions.length) {
              // Deselect all
              if (onSelectedIdsChange) onSelectedIdsChange(new Set());
              else setInternalSelectedIds(new Set());
            } else {
              const all = new Set(suggestions.map((s) => s.checklistItemId));
              if (onSelectedIdsChange) onSelectedIdsChange(all);
              else setInternalSelectedIds(all);
            }
          }}
          className="text-[11px] text-accent hover:text-accent/80 transition-colors cursor-pointer"
        >
          {currentSelectedIds.size === suggestions.length
            ? t("upload.suggestions.deselectAll")
            : t("upload.suggestions.selectAll")}
        </button>
      </div>

      <div className="space-y-4">
        {categoryGroups.map((group) => (
          <div key={group.categoryId}>
            <div className="flex items-center gap-2 mb-1.5 px-0.5">
              <p className="text-[11px] font-medium text-secondary">
                {group.categoryName}
              </p>
              <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400 border border-blue-500/15">
                {group.auditName}
              </span>
            </div>
            <div className="space-y-2">
              {group.suggestions.map((suggestion) => (
                <SuggestionCard
                  key={suggestion.checklistItemId}
                  suggestion={suggestion}
                  isSelected={currentSelectedIds.has(
                    suggestion.checklistItemId,
                  )}
                  onToggle={() => toggleSelection(suggestion.checklistItemId)}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2 pt-1">
        {showConfirmButton ? (
          <Button
            onClick={handleConfirm}
            disabled={isAccepting || currentSelectedIds.size === 0}
            className="flex-1"
          >
            {isAccepting ? (
              <Loader2 size={14} className="animate-spin mr-1.5" />
            ) : null}
            {t("upload.suggestions.confirm", {
              count: currentSelectedIds.size,
            })}
          </Button>
        ) : null}

        <Button variant="ghost" onClick={handleDismiss} disabled={isAccepting}>
          {t("upload.suggestions.dismissAll")}
        </Button>
      </div>
    </div>
  );
}
